import maplibregl, * as M from 'maplibre-gl';
import * as turf from '@turf/turf';
import circle from '@turf/circle';

import {
   MapStyle,
   ViewerState
} from '../common/managers/Types';
import { MitLatLng, GenerateGeomInterface, MitLayerHandle, MapFacadeAbstract, CircleSelectorAbstract, PriotisedMapLayerTypes } from './MapFacade';
import { GenerateGeom } from './GenerateGeom';
import { SettingsManager } from '@viamap/viamap2-common';
import { FeatureLayer } from './WmsLayerFunc';
import { isGeodanmarkSliderWmsTileUrl } from './HistoricalOrthophoto';
import { GeodanmarkTileFetchQueue } from './GeodanmarkTileFetchQueue';
import { createDonutChart } from './PieChartDonut';
import { MapImageCache } from './MapImageCache';
import { VectorLayer } from './VectorLayerFunc';
import { Spider } from './Spider';
import { BuiltInVectorSources, getMapStyle, getVectorSource } from './LinkToMapStyle';
import { GeojsonWFS } from './WFSGeojsonLayerFunc';
import { WFStoGeoJson } from 'src/functions/WFSutils';
import { DynamicDataDrivenLayer, DynamicDataDrivenLayerPropertyInfo } from './DynamicDataDrivenLayer';
import { GlyphProtocolHandler, stylerSetGlyphProtocol } from './MapFontManager';
import { DC } from 'src/dataCollector/DataCollector';
import { GenerateGeomUtils } from './GenerateGeomUtils';

// export type MitEvent = M.MapboxEvent;

enum SelectionState { Disabled, ClickToSelectCenter, CenterSelected, RimSelected, WaitingForServer }



export class MitLayerHandleMaplibre implements MitLayerHandle {
   source: M.SourceSpecification | null = null;
   sourceId: string = "";
   layerList: M.LayerSpecification[] = [];
   // layersConsumingEvents:string[]=[];
   layersWithPieCharts: string[] = [];

   static getUniqueId() {
      // todo: implement
   }

   constructor() {
      // super();
   }

   hasItems(): boolean {
      // todo implement;
      return false;
   }

   clearLayers(map: MapFacadeAbstract) {
      map?.removeLayer?.(this);

      this.source = null;
      this.layerList = [];
      this.layersWithPieCharts = [];
      this.sourceId = "";
   }

   updateSource(map: MapFacadeMaplibre, source: M.GeoJSONSourceSpecification["data"] | M.ImageSourceSpecification) {
      switch (this.source?.type) {
         case "geojson": {
            let goejsonSourceData = source as M.GeoJSONSourceSpecification["data"];
            (map.getMapPop().getSource(this.sourceId) as maplibregl.GeoJSONSource)?.setData(goejsonSourceData);
            this.source.data = goejsonSourceData;
            return
         }
         case "image": {
            let imageSourceSpec = source as M.ImageSourceSpecification;
            (map.getMapPop().getSource(this.sourceId) as maplibregl.ImageSource)?.setCoordinates(imageSourceSpec.coordinates);
            this.source.coordinates = imageSourceSpec.coordinates;
            return
         }
         case "raster-dem":
         case "video":
         case "raster":
         case "vector": {
            console.error("Update source not implemented for source type: " + this.source?.type);
         }
      }
   }

   addSource(id: string, source: M.SourceSpecification) {
      this.source = source; this.sourceId = id;
   }
   hasSource() {
      return Boolean(this.source && this.sourceId);
   }


   getSourceId() {
      return this.sourceId;
   }

   getSource() {
      return this.source;
   }

   addLayer(layer: any) {
      this.layerList.push(layer);
   }
   hasLayers() {
      return Boolean(this.layerList && this.layerList.length > 0);
   }
   getLayers() {
      return this.layerList;
   }
   getLayerIds() {
      // tslint:disable-next-line
      return this.layerList.map((layer) => { return layer['id']; });
   }
   remove() {
      // todo: implement
   }

   addLayerConsumingEvents(_id: string) {
      // this.layersConsumingEvents.push(id); 
   }
   getLayersConsumingEvents(): string[] {
      // return this.layersConsumingEvents;
      return []
   }

   moveLayer(map: MapFacadeAbstract) {
      map.moveLayer(this);
   }

   addTo(map: MapFacadeAbstract)
   addTo(map: MapFacadeAbstract, before: MitLayerHandle[])
   addTo(map: MapFacadeAbstract, before: MitLayerHandle[] | undefined)
   addTo(map: MapFacadeAbstract, before?: MitLayerHandle[]) {
      if (before?.length) {
         map.addLayer(this, before);
      } else {
         map.addLayer(this)
      }
   }

   addlayersWithPieCharts(id: string) {
      this.layersWithPieCharts.push(id);
   }

}

type mapFacadeOptions = {
   mapStyle?: MapStyle
}
export class MapFacadeMaplibre implements MapFacadeAbstract {

   public readonly ControlPosition = {
      TopRight: "top-right",
      TopLeft: "top-left",
      BottomRight: "bottom-right",
      BottomLeft: "bottom-left"
   };

   private static readonly GEODANMARK_TILE_TIMEOUT_MS = 2000;
   private static readonly GEODANMARK_TILE_MAX_CONCURRENT = 3;
   private static readonly GEODANMARK_TILE_MAX_ATTEMPTS = 4;
   private static readonly GEODANMARK_TILE_RETRY_DELAY_MS = 4000;
   private readonly geodanmarkTileQueue = new GeodanmarkTileFetchQueue(MapFacadeMaplibre.GEODANMARK_TILE_MAX_CONCURRENT);
   private readonly geodanmarkTileAttempts = new Map<string, number>();
   private urlToLabels: { urlIdent: string, label: string, x: number, y: number }[] = [];
   private map: M.Map;
   private loadingMoveEnd;
   private mapLayoutRestore: { [key: string]: "visible" | "none" | undefined } = {};
   private _isInitialized = false;
   private generateGeomObj: GenerateGeomInterface | undefined;
   private markersBySourceId: { [sourceId: string]: { [id: number]: M.Marker } } = {};
   private layerControl: undefined;
   private catchment: MitLayerHandleMaplibre | undefined;
   private attributionList: string[] = [];
   private rotationCallbackHandler: ((rotation: number) => void) | undefined;
   private moveendCallbackHandler: ((viewState: ViewerState) => void) | undefined;
   private moveendFeatureLayer: { sourceID: string, minZoom: number, dataGetter: (bounds) => any, dataTransformer: (data, options: { zoom: any, bounds: any }) => any }[] = [];

   // store the map configuration properties in an object,
   // we could also move this to a separate file & import it if desired.
   // private config:L.MapOptions = { 
   // //                    center: [55.486051, 10.513315], // Odense Fjord
   // //                    zoom: 10, // start zoom
   //                     zoomControl: false,
   //                     center: [56.304411, 10.508832], // Ved Rønde på Djursland
   //                     zoom: 8, // start zoom
   //                     maxZoom: Utils.getSystemSetting("maxZoom", 18),
   //                     minZoom: Utils.getSystemSetting("minZoom", 3),
   // //                    scrollwheel: false,
   // //                    legends: true,
   // //                    infoControl: false,
   //                     attributionControl: false,
   //                     renderer: L.canvas()
   //                     //  preferCanvas: true, Note canvas performs better but IE is not supported
   //                 };

   constructor(containerElement: string | HTMLElement, _isOrthophotoGlobal?: boolean, options?: (Partial<M.MapOptions> & mapFacadeOptions)) {

      let defaultBounds = SettingsManager.getSystemSetting("MapInitializationBounds", [[7.741269070919969, 57.808475243053806], [13.511745485770007, 54.47934487366999]]);

      let tileTimeoutSeconds = SettingsManager.getSystemSetting("tileTimeoutSeconds", 5);
      let tileTimeout = tileTimeoutSeconds * 1000; // convert to milliseconds

      let style = this.mapStyleStyle(options?.mapStyle?.style, options?.mapStyle?.mods ?? [])
      let source = getVectorSource(options?.mapStyle?.source);

      var map2 = new M.Map({
         container: containerElement,
         hash: true,
         style: { ...style, sources: source } as any,
         trackResize: true,
         validateStyle: true,
         bounds: defaultBounds,
         transformRequest: (url, resourceType) => {
            if (resourceType === 'Tile' && url.startsWith('http://13.48.91.12:8080/geoserver')) {
               return {
                  url: url,
                  headers: { 'Origin': '*' }
               };
            } else {
               return {
                  url: url,
               };
            }
         }
         , ...options
      })


      // document.getElementById("Mit-MapOverlay").append(control.onAdd(map2))


      map2.on('load', () => {
         this._isInitialized = true;
         const allMapLayers = map2.getStyle().layers
         const viavectorLayers = allMapLayers.filter((a) => (a && ["viavector", "hillshade", "orthophoto"].includes(a["source"])));
         viavectorLayers.forEach((a) => {
            this.mapLayoutRestore[a.id] = a.layout?.visibility as ("visible" | "none") | undefined
         })
      })
      this.initEventManagers(map2);
      map2.addControl(new M.NavigationControl({}), SettingsManager.getSystemSetting("NavigationControlLocation"));
      map2.addControl(new M.ScaleControl({}), 'bottom-left');
      // ----------------------------------- load icons ----------------------------------
      let scale = 1;
      map2.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png`).then((image) => {
         map2.addImage('blue-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)

      })

      map2.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png`).then((image) => {
         map2.addImage('red-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
      })


      let legImg = Spider.generateLegImage(
         [0, 0],
         [100, 100],
         2,
         "black",
      );

      map2.addImage('spider-line', legImg, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
      maplibregl.addProtocol("glyphs", GlyphProtocolHandler);
      maplibregl.addProtocol("proxy", async (params, abortController): Promise<M.GetResourceResponse<{}>> => {
         const corsProxy = SettingsManager.getSystemSetting("ImageCorsProxyUrl")

         const resp = fetch(corsProxy + `https://${params.url.split("://")[1]}`, {
            headers: { "Accept": "image/png" },
            signal: abortController.signal
         })
         let x = setTimeout(() => {
            if (abortController.signal.aborted) return;
            abortController.abort("Timeout")
         }, tileTimeout)
         try {
            const awaited = await resp
            clearTimeout(x)

            if (awaited.status == 200) {
               if (awaited.headers.get("Content-Type")?.includes("text")) {
                  return { data: await generateTileDataWithText() }
               }
               return { data: await awaited.arrayBuffer() }
            } else {
               return { data: await generateTileDataWithText("Error: " + awaited.statusText) }
            }
         } catch (error) {
            console.error("Timeout error: " + error)
            return { data: await generateTileDataWithText() }
         }
      });

      maplibregl.addProtocol("normal", async (params, abortController: AbortController): Promise<M.GetResourceResponse<{}>> => {
         if (params.url.includes("glyph")) {
            console.log("Glyph url: " + params.url);
         }

         if (isGeodanmarkSliderWmsTileUrl(params.url)) {
            return this.geodanmarkTileQueue.fetch(() =>
               this.fetchGeodanmarkSliderTile(params.url, abortController)
            );
         }

         const resp = fetch(`https://${params.url.split("://")[1]}`, {
            headers: { "Accept": "*/*" },
            signal: abortController.signal
         })
         let x = setTimeout(() => {
            if (abortController.signal.aborted) return;
            abortController.abort("Timeout")
         }, tileTimeout)
         try {
            const awaited = await resp
            clearTimeout(x)
            if (awaited.status == 200) {
               if (awaited.headers.get("Content-Type")?.includes("text")
                  || awaited.headers.get("Content-Type")?.includes("html")
                  || awaited.headers.get("Content-Type")?.includes("json")
                  || awaited.headers.get("Content-Type")?.includes("xml")
               ) {
                  return this.handleWmsTileFailure(params.url);
               }
               return { data: await awaited.arrayBuffer() }
            } else {
               return this.handleWmsTileFailure(params.url);
            }
         } catch (error) {
            console.error("Timeout error: " + error)
            return this.handleWmsTileFailure(params.url);
         }
      });

      this.map = map2;
   }

   /**
    * Registers a callback handler for when a rotation event occurs.
    * @param rotationHandler Function to occur on rotation event.
    */
   public registerRotationCallbackHandler(rotationHandler: (rotation: number) => void) {

      this.rotationCallbackHandler = rotationHandler;

   }

   public setUrlToLabels(a: typeof this.urlToLabels) {
      this.urlToLabels = a
   }

   public readLabelFromUrl(url: string) {
      let label = this.urlToLabels.find((a) => url.includes(a.urlIdent))
      return label
   }

   private async fetchGeodanmarkSliderTile(
      tileUrl: string,
      mapAbort: AbortController,
   ): Promise<M.GetResourceResponse<{}>> {
      const priorAttempts = this.geodanmarkTileAttempts.get(tileUrl) ?? 0;
      if (priorAttempts >= MapFacadeMaplibre.GEODANMARK_TILE_MAX_ATTEMPTS) {
         return { data: blankTileData() };
      }

      const fetchAbort = new AbortController();
      const onMapAbort = () => fetchAbort.abort();
      mapAbort.signal.addEventListener("abort", onMapAbort, { once: true });

      const timeoutId = setTimeout(() => fetchAbort.abort(), MapFacadeMaplibre.GEODANMARK_TILE_TIMEOUT_MS);
      try {
         const response = await fetch(`https://${tileUrl.split("://")[1]}`, {
            headers: { Accept: "*/*" },
            signal: fetchAbort.signal,
         });
         clearTimeout(timeoutId);
         mapAbort.signal.removeEventListener("abort", onMapAbort);

         if (response.status === 200 && isWmsImageResponse(response)) {
            this.geodanmarkTileAttempts.delete(tileUrl);
            return { data: await response.arrayBuffer() };
         }
         return this.geodanmarkSliderTileFailure(tileUrl);
      } catch (error) {
         clearTimeout(timeoutId);
         mapAbort.signal.removeEventListener("abort", onMapAbort);
         if (mapAbort.signal.aborted) {
            throw error;
         }
         return this.geodanmarkSliderTileFailure(tileUrl);
      }
   }

   private geodanmarkSliderTileFailure(tileUrl: string): M.GetResourceResponse<Uint8Array> {
      const attempts = (this.geodanmarkTileAttempts.get(tileUrl) ?? 0) + 1;
      this.geodanmarkTileAttempts.set(tileUrl, attempts);
      if (attempts >= MapFacadeMaplibre.GEODANMARK_TILE_MAX_ATTEMPTS) {
         return { data: blankTileData() };
      }
      return {
         data: blankTileData(),
         expires: new Date(Date.now() + MapFacadeMaplibre.GEODANMARK_TILE_RETRY_DELAY_MS),
      };
   }

   /** Other WMS: legacy error tile (no automatic retry). */
   private async handleWmsTileFailure(tileUrl: string) {
      const urlIdent = urlIdentFromUrl(tileUrl);
      const info = this.readLabelFromUrl(urlIdent);
      return { data: await generateTileDataWithText(info?.label, info?.x, info?.y) };
   }

   public restoreNormalVisibility() {
      let layer = Object.keys(this.mapLayoutRestore)
      layer.forEach((a) => {
         this.map.setLayoutProperty(a, 'visibility', this.mapLayoutRestore[a] || "visible")
      })
   }

   public updateMarkers(sourceId: string) {
      const features = this.map.querySourceFeatures(sourceId);

      if (!this.markersBySourceId[sourceId]) {
         return //should do nothing
      } else {
         // remove all old markers
         Object.values(this.markersBySourceId[sourceId]).forEach((m) => {
            m.remove();
         });
      }

      // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
      // and add it to the map if it's not there already
      for (let i = 0; i < features.length; i++) {
         const coords = (features[i].geometry as any).coordinates;
         const props = features[i].properties;
         if (!props.cluster) continue;
         const id = props.cluster_id;

         let marker = this.markersBySourceId[sourceId] && this.markersBySourceId[sourceId][id];
         if (marker) {
            // remove old marker
            marker.remove();
         }
         // create marker
         const el = createDonutChart(props);
         marker = new maplibregl.Marker({
            element: el as any
         }).setLngLat(coords);
         marker.addTo(this.map);
         this.markersBySourceId[sourceId][id] = marker;
      }
   }

   /**
    * Registers a callback handler for when a moveend event occurs.
    * @param moveendHandler Function to occur on moveend event.
    * sends a state
    * {
         center:[number, number],
         pitch:number,
         zoom:number,
         bearing:number
      }
    */
   public registerMoveendCallbackHandler(moveendHandler: (viewerState: ViewerState) => void) {
      this.moveendCallbackHandler = moveendHandler;
   }

   public customLoading() {
      return this.loadingMoveEnd;
   }

   public initEventManagers(map: M.Map) {
      map.on('styleimagemissing', (e: any) => {
         if (!e.id || !e.type) {
            return;
         }
         let id = e.id; // id of the missing image
         // TODO: Move following id.startsWith to ImageGeneration Manager
         if (id.startsWith("mit-pin")) {
            const color = id.split("mit-pin-")[1]
            GenerateGeom.GetEveryMitPin(color, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("NS-pin")) {
            const color = id.split("NS-pin-")[1]
            GenerateGeom.GetEveryNSPin(color, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("mit-poi-")) {
            const icon = id.split("mit-poi-")[1]
            GenerateGeom.GetEveryMitPOI(icon, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("NS-poi-")) {
            const icon = id.split("NS-poi-")[1]
            GenerateGeom.GetEveryNSPOI(icon, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("mit-patt-")) {
            GenerateGeom.getEveryMitPattern(id, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         // Handle React Icons registered via GenerateGeom.registerReactIcon()
         if (id.startsWith("react-icon-")) {
            GenerateGeom.generateReactIconImage(id, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading React Icon image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }

         MapImageCache.loadImage(id, (image) => {
            if (map.hasImage(id))
               return
            map.addImage(id, image);
         })
      });
      map.on('error', (ev) => {
         if ("sourceId" in ev && typeof ev.sourceId == "string" && ev.sourceId.includes("backgroundlayer")) {
            if ("source" in ev) {
               let message = ErrorMessageMapfacade(ev.source as M.Source)
               document.getElementById("NonBlockingError")?.dispatchEvent(new CustomEvent("NonBlockingError", { "detail": message }))
            }
         }

         console.error("Maplibre error: " + ev.error.message)
      })
      map.on('rotate', () => {
         this.rotationCallbackHandler?.(-map.getBearing());
      });

      map.on('moveend', () => {
         let cen = new MitLatLng(map.getCenter().lat, map.getCenter().lng)
         const state = {
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            zoom: map.getZoom(),
            center: cen
         };
         this.moveendCallbackHandler?.(state);
      });
      map.once('idle', () => {
         let lHandle = new MitLayerHandleMaplibre()
         lHandle.addSource("idTest", { type: "geojson", data: turf.featureCollection([]) });
         lHandle.addLayer({
            id: "testtt",
            type: 'line',
            source: "idTest",
            paint: {
               "line-color": ["match",
                  ["get", "anvendelse"],
                  "S", "red",
                  "VV", "blue",

                  "green"
               ],
               'line-width': 3,
            }
         })
         lHandle.addTo(this);
      })

      map.on('moveend', async (e) => {
         this.loadingMoveEnd = true;
         let bounds = e.target.getBounds();
         let zoom = e.target.getZoom();
         let end = this.moveendFeatureLayer.map(async (a) => {
            if (a.minZoom > zoom) {
               return
            }
            a.dataGetter(bounds).then(async (data) => {
               (this.map.getSource(a.sourceID) as M.GeoJSONSource | undefined)?.setData(
                  await a.dataTransformer?.(data, { zoom: zoom, bounds: bounds }) || data
               )
            })
         })
         await Promise.all(end);
         this.loadingMoveEnd = false;
      })
      // ----------------- PIE MARKERS -----------------



      // ----------------- PIE MARKERS -----------------


      // code for creating an SVG donut chart from feature properties



      // ----------------- PIE MARKERS -----------------
      // after the GeoJSON data is loaded, update markers on the screen and do so on every map move/moveend
      map.on('data', (e: any) => {
         if (!e.sourceId || !(e.sourceId as string).startsWith("layer-source-pie") || !e.isSourceLoaded) {
            return;
         }
         // update this specific layer from source
         this.updateMarkers(e.sourceId as string);

         const updateAllPieMarkers = () => {
            Object.keys(this.markersBySourceId).forEach((sourceId) => {
               this.updateMarkers(sourceId);
            })
         }
         map.on('move', () => {
            updateAllPieMarkers()
         });
         map.on('moveend', () => {
            updateAllPieMarkers()
         });
      });

      // ----------------- PIE MARKERS END -----------------

   }

   public setMinZoom(minZoom?: number | null) {
      return this.map?.setMinZoom?.(minZoom)
   }

   public setMaxZoom(maxZoom?: number | null) {
      return this.map?.setMaxZoom?.(maxZoom)
   }

   public isInitialized(): boolean {
      return Boolean(this._isInitialized);
   }

   public setFeatureState(feature, state) {
      this.map.setFeatureState(feature, state)
   }

   removeSource(source: string) {
      this.map.removeSource(source)
   }

   // public getGenerateGeom() {
   //    return this.generateGeomObj || (this.generateGeomObj = new GenerateGeom(), this.generateGeomObj);
   // }

   public getCircleSelector() {
      return null!;
   }

   public addZoomControl() {
      this.map?.addControl(
         new M.NavigationControl(
            {
               'showCompass': true,
               'showZoom': true
            }
         ),
         'top-left'
      );
   }

   public getZoomControl(): string {
      // let c = new DOMParser().parseFromString(
      //    `
      // <div class="mapboxgl-ctrl mapboxgl-ctrl-group">
      //    <button class="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in" type="button"
      //       title="Zoom in" aria-label="Zoom in">
      //    </button>
      //    <button class="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
      //       type="button" title="Zoom out" aria-label="Zoom out">
      //    </button>
      //    <button
      //       class="mapboxgl-ctrl-icon mapboxgl-ctrl-compass" type="button" title="Reset bearing to north"
      //       aria-label="Reset bearing to north">
      //       <span class="mapboxgl-ctrl-compass-arrow"
      //          style="transform: rotate(0deg);">
      //       </span>
      //    </button>
      // </div>
      //    `, 
      //    'text/xml');
      // return c.body;
      return `
      <div id="mit-container-top-left" className="mit-control-container">
      <div className="mapboxgl-ctrl mapboxgl-ctrl-group">
<button className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in" type="button"
  title="Zoom in" aria-label="Zoom in">
</button>
<button className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
  type="button" title="Zoom out" aria-label="Zoom out">
</button>
<button
  className="mapboxgl-ctrl-icon mapboxgl-ctrl-compass" type="button" title="Reset bearing to north"
  aria-label="Reset bearing to north">
  <span className="mapboxgl-ctrl-compass-arrow">
  </span>
</button>
</div>
      `;
   }

   public addScaleControl() {
      // this.map && this.map.addControl(new M.ScscaleControl({}), 'bottom-left');
   }

   /**
    * @deprecated use switchMapStyle
    */
   public setBackgroundLayer(layer?: string) {
      if (layer === undefined || layer === null || layer === "") {
         return;
      }
      if (this.map) {
         // Switch on Areal Image
         switch (layer) {
            case 'Orthophoto': {
               this.restoreNormalVisibility()
               this.map.setLayoutProperty('orthophoto', 'visibility', 'visible');
               this.map.setLayoutProperty('building', 'visibility', 'none');
               this.map.setLayoutProperty('building-3d', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean_lowzoomonly', 'visibility', 'visible');
               this.map.setLayoutProperty('water_ocean', 'visibility', 'none');
               this.map.getStyle().layers!.forEach(layer2 => {
                  // tslint:disable-next-line
                  if (layer2["source-layer"] && layer2["source-layer"] === 'transportation') {
                     // tslint:disable-next-line
                     this.map.setLayoutProperty(layer2["id"], 'visibility', 'none');
                  }
               });
               break;
            }
            case 'OrthophotoNoWaterMask': {
               this.restoreNormalVisibility()
               this.map.setLayoutProperty('orthophoto', 'visibility', 'visible');
               this.map.setLayoutProperty('building', 'visibility', 'none');
               this.map.setLayoutProperty('building-3d', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean_lowzoomonly', 'visibility', 'none');
               this.map.getStyle().layers!.forEach(layer2 => {
                  // tslint:disable-next-line
                  if (layer2["source-layer"] && layer2["source-layer"] === 'transportation') {
                     // tslint:disable-next-line
                     this.map.setLayoutProperty(layer2["id"], 'visibility', 'none');
                  }
               });
               break;
            }
            case 'Map': {
               this.restoreNormalVisibility();
               break;
            }
            case 'BlankMap': {
               let layer = Object.keys(this.mapLayoutRestore)
               layer.forEach((a) => {
                  this.map.setLayoutProperty(a, 'visibility', "none")
               })
               break;
            }
            default:
               throw new Error("Unexpected background layer type: " + layer);
         }
      }
   }

   public applyMapStyleMods(style: any[], mods?: string[]): any[] {
      let newStyle = style.map((layer) => {
         return mods?.reduce((acc, mod) => {
            return this.modLayer(acc, mod)
         }, layer) ?? layer;
      });
      return newStyle ?? [];
   }

   private modLayer(layer: any, mod: string) {
      switch (mod) {
         case "Ortophoto": {
            if (layer.id == "orthophoto") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean_lowzoomonly") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "building-3d") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "OrthophotoNoWaterMask": {
            if (layer.id == "orthophoto") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean_lowzoomonly") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "water_ocean") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "building-3d") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "No3D": {
            if (layer.type == "fill-extrusion") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoText": {
            if (layer.type == "symbol") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoRoads": {
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoBuildings": {
            if (layer["source-layer"] === "building") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoAeroways": {
            if (layer["source-layer"] === "aeroway") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "HillShadows": {
            if (layer.id == "hillshade") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            break;
         }
      }
      return layer
   }


   public mapStyleStyle(style?: string | object, mods?: string[]): object {
      if (style) {
         if (typeof style == "object") {
            return stylerSetGlyphProtocol({ ...style, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(style["layers"]), mods ?? []) });
         } else if (style.startsWith("background-")) {
            let defaultStyle = getMapStyle()
            return stylerSetGlyphProtocol({ ...defaultStyle, layers: this.applyMapStyleMods(this.addPartSectionIfMissing([{ id: "background", type: "background", paint: { 'background-color': style.split("-").at(-1) ?? "white" } }]), mods ?? []) });
         } else {
            let styleRes = getMapStyle(style)
            return stylerSetGlyphProtocol({ ...styleRes, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(styleRes["layers"]), mods ?? []) });
         }
      } else {
         let styleRes = getMapStyle()
         return stylerSetGlyphProtocol({ ...styleRes, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(styleRes["layers"]), mods ?? []) });
      }
   }

   public async MapSource(builtInSources?: BuiltInVectorSources | string) {
      let sourceUrl = getVectorSource(builtInSources)
      if (typeof sourceUrl == "string" && sourceUrl.startsWith("http")) {
         let source = await fetch(sourceUrl);
         if (!source.ok) {
            console.error("Error loading vector source: " + source.statusText)
            return;
         }
         let sourceJson = await source.json();
         Object.keys(sourceJson).forEach((key) => {
            (this.map.getSource(key) as M.VectorTileSource).setUrl(sourceJson[key].url)
         })
      } else {
         let sourceJson = sourceUrl
         Object.keys(sourceJson).forEach((key) => {
            (this.map.getSource(key) as M.VectorTileSource).setUrl(sourceJson[key].url)
         })
      }
   }

   public async switchMapStyle(style: string | object, mods?: string[]) {
      if (typeof style == "object") {
         if (style["layers"]) {
            this.setMapStyleDiff(style, mods)
            return
         } else {
            this.setMapStyleDiff({ layers: style }, mods)
         }
         return
      }
      if (style.startsWith("http")) {
         await fetch(style).then((response) => {
            if (response.ok) {
               return response.json()
            } else {
               console.error("Error loading map style: " + response.statusText)
            }
         }).then((a) => {
            this.setMapStyleDiff(a, mods)
         })
         return
      }
      if (style.startsWith("background-")) {
         let backgroundStyle = {
            layers: [{ id: "background", type: "background", paint: { 'background-color': style.split("-").at(-1) ?? "white" } }]
         } as any;
         this.setMapStyleDiff(backgroundStyle, mods)
         return
      }
      let styleObj = getMapStyle(style)
      this.setMapStyleDiff(styleObj, mods)

   }

   private setMapStyleDiff(newStyle: any, mods?: string[]) {
      let newParts = this.splitLayersIntoParts(this.applyMapStyleMods(this.addPartSectionIfMissing(newStyle.layers), mods))
      let oldParts = this.splitLayersIntoParts(this.map.getStyle().layers!)

      let parts = [...new Set([...Object.keys(newParts), ...Object.keys(oldParts)])];
      let added: any[] = []
      parts.forEach((a) => {
         this.diffForPart(newParts[a] ?? [], oldParts[a] ?? [], added)
         added = [...added, ...newParts[a].map((a) => a.id) ?? []]
      })
   }

   private diffForPart(newLayers, oldLayers, notAlreadyAdded?: string[]) {
      let unusedReplacement = newLayers
      let lastLayerId = newLayers.at(-1)?.id ?? "";
      let wrongOrder = false;
      oldLayers.forEach((layer, idx) => {
         if (newLayers[idx] && newLayers[idx].id != layer.id) wrongOrder = true;
         let replaceMent = newLayers.find((b) => b.id == layer.id)
         if (replaceMent) {
            unusedReplacement = unusedReplacement.filter((b) => b.id !== layer.id)
            this.replacePaint(layer, replaceMent.paint)
            this.replaceLayout(layer, replaceMent.layout)
            this.replaceFilter(layer, replaceMent.filter)
            this.replaceMinMax(layer, replaceMent.minzoom, replaceMent.maxzoom)
         } else {
            if (notAlreadyAdded?.includes(layer.id)) {
               return
            }
            this.map.removeLayer(layer.id)
         }
      })
      if (lastLayerId) {
         unusedReplacement.forEach((layer) => {
            if (layer.id == lastLayerId) return
            if (this.map.getLayer(layer.id)) {
               this.map.removeLayer(layer.id)
            }
            this.map.addLayer(layer, lastLayerId);
         })
         if (wrongOrder) {
            let pre = newLayers.at(-1);
            newLayers.reverse().forEach((layer) => {
               if (layer.id != pre?.id) {
                  this.map.moveLayer(layer.id, pre?.id)
                  pre = layer
               }
            })
         }
      }
   }

   private addPartSectionIfMissing(layer: any[]) {
      let missingLayers = ["part-1-end", "part-2-start", "part-2-end"]
      if (layer[0].id !== "part-1-start") {
         layer.unshift(partLayer("part-1-start"))
      }
      layer.forEach((a) => {
         if (missingLayers.includes(a.id)) {
            missingLayers = missingLayers.filter((b) => b != a.id)
         }
      })
      missingLayers.forEach((a) => {
         layer.push(partLayer(a))
      })
      return layer
   }

   private splitLayersIntoParts(layers: any[]) {
      let startEndRegex = new RegExp(/^part-(\d+)-(?:start|end)$/)
      let currentPart: string = layers[0].id ?? "" //Handles styles that doen't use part-1-start/end
      let partToLayers: { [key: string]: any[] } = { [currentPart]: [] }
      layers.forEach((layer) => {
         if (layer.id.endsWith("-start")) {
            currentPart = startEndRegex.exec(layer.id)?.[0] ?? ""
            if (currentPart) {
               partToLayers[currentPart] = [layer]
               return
            }
         }
         if (currentPart) {
            partToLayers[currentPart].push(layer)
         }
         if (layer.id.endsWith("-end")) {
            currentPart = ""
         }
      })
      return partToLayers
   }

   public addAttribution(att: any) {
      // this.map.addControl(new M.AttributionControl({
      //    customAttribution: [ att ]
      // }));
      this.attributionList.push(att);
   }

   /**
    * @
    * @deprecated true
    * @returns 
    */
   public getMapPop() {
      //TODO: Create way to add popups without getMap()
      return this.map
   }

   // public toggleLayerVisibility(layerHandle:L.Layer):boolean {
   //    let map = this.getMap();
   //    let isVisibleAfter;
   //    if(map.hasLayer(layerHandle)) {
   //       map.removeLayer(layerHandle);
   //       isVisibleAfter = false;
   //   } else {
   //       map.addLayer(layerHandle);        
   //       isVisibleAfter = true;
   //   }
   //   return isVisibleAfter;
   // }

   public setLayerVisibility(layerHandle: MitLayerHandle, show: boolean) {
      layerHandle.getLayerIds()?.forEach((layerId) => {
         this.map?.setLayoutProperty(layerId, 'visibility', show ? 'visible' : 'none');
      });
      layerHandle.layersWithPieCharts.forEach((sourceId) => {
         if (show) {
            this.markersBySourceId[sourceId] = {}
         } else {
            Object.keys(this.markersBySourceId[sourceId] || {}).forEach((marker) => this.markersBySourceId[sourceId][marker].remove())
            delete this.markersBySourceId[sourceId]
         }
      })
   }

   public getLayerVisibility(layerHandle: MitLayerHandle): boolean {
      // todo: Implement
      let layerId = layerHandle.getLayerIds()[0];
      // todo: this always returns undefined.
      let result = this.map && this.map.getLayoutProperty(layerId, 'visibility');
      return result;
   }

   public setViewerState(newState: ViewerState) {
      // wait a bit to mitigate bug in Leaflet when changing view.
      let waitMilliSeconds = 500;
      setTimeout(
         () => {
            this.map?.panTo(MitLatLng.fromL(newState.center).getLngLat());
            setTimeout(
               () => {
                  if (newState?.zoomFactor) {
                     this.map?.setZoom(newState.zoomFactor);
                  }
               },
               waitMilliSeconds);
         },
         waitMilliSeconds);
   }

   public easeTo(options: any, eventData?: any): void {
      this.map?.easeTo?.(options, eventData);
   }

   public fire(event: any, eventData?: any): void {
      this.map?.fire?.(event, eventData);
   }

   public zoomToFeature(target: MitLayerHandle, padding?: number) {
      if (!target.hasSource()) {
         throw new Error("zoomToFeature: MitLayerHandleMaplibre has no source");
      }
      const source = target.getSource() as any;
      if (!source) {
         throw new Error("zoomToFeature: source not found");
      }
      if (source.type === "image" && Array.isArray(source.coordinates)) {
         this.zoomToCoordinates(source.coordinates, padding);
         return;
      }
      if (source.data) {
         this.zoomToData(source.data, padding);
         return;
      }
      throw new Error("zoomToFeature: unsupported source type for zoom");
   }

   public setData(layer: MitLayerHandle, geoJson: any): void {
      if (layer.hasSource()) {
         (this.map?.getSource(layer.sourceId) as M.GeoJSONSource | undefined)?.setData(geoJson)
      }
   }

   public zoomToData(data: any, padding?: number) {
      if (!data) {
         console.warn("zoomToData called without data");
         return;
      }
      let boundingBox = turf.bbox(data);
      this.fitBoundsWithPadding(
         ((boundingBox.length === 4) ? boundingBox : boundingBox.splice(0, 4)) as [number, number, number, number],
         padding
      );
   }

   private zoomToCoordinates(coords: [number, number][], padding?: number) {
      if (!coords || coords.length === 0) {
         console.warn("zoomToCoordinates called without coordinates");
         return;
      }
      const lngs = coords.map((coord) => coord[0]);
      const lats = coords.map((coord) => coord[1]);
      const bounds: [number, number, number, number] = [
         Math.min(...lngs),
         Math.min(...lats),
         Math.max(...lngs),
         Math.max(...lats),
      ];
      this.fitBoundsWithPadding(bounds, padding);
   }

   private fitBoundsWithPadding(bounds: [number, number, number, number], padding?: number) {
      try {
         this.map?.fitBounds(
            ((bounds.length === 4) ? bounds : bounds.slice(0, 4)) as [number, number, number, number],
            {
               padding: { top: padding ?? 25, bottom: padding ?? 25, left: padding ?? 25, right: padding ?? 25 },
               maxZoom: 19
            }
         );
      } catch (err: any) {
         const evv = new CustomEvent('mit-error-handler', { detail: err.message || err });
         document.body.dispatchEvent(evv);
      }
   }

   public zoomToDataAvoidingWindows(data: any, padding?: number, debug?: boolean) {
      if (!this.map) {
         return;
      }

      if (debug) {
         this.clearDebugOverlays();
      }

      try {
         const dataBbox = turf.bbox(data);
         const bboxArray = (dataBbox.length === 4 ? dataBbox : dataBbox.slice(0, 4)) as [number, number, number, number];
         const dataBounds: [[number, number], [number, number]] = [
            [bboxArray[0], bboxArray[1]],
            [bboxArray[2], bboxArray[3]]
         ];

         const mapContainer = this.map.getContainer();
         const containerRect = mapContainer.getBoundingClientRect();
         const containerWidth = Math.max(0, Math.round(containerRect.width));
         const containerHeight = Math.max(0, Math.round(containerRect.height));

         const windowElements = Array.from(document.querySelectorAll('.AdvancedDragModal')) as HTMLElement[];
         const visibleWindows = windowElements.filter(el => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0 && !el.classList.contains('js_minimized');
         });

         const requestedPadding = Math.max(0, Math.round(padding ?? 15));
         const maxReasonablePadding = Math.floor(Math.min(containerWidth, containerHeight) / 2);
         const defaultPadding = Math.min(requestedPadding, maxReasonablePadding);
         let availableViewport = {
            left: defaultPadding,
            top: defaultPadding,
            right: containerWidth - defaultPadding,
            bottom: containerHeight - defaultPadding
         };

         const windowRects: Array<{ left: number; top: number; right: number; bottom: number }> = [];

         if (visibleWindows.length > 0) {

            for (const windowEl of visibleWindows) {
               const rect = windowEl.getBoundingClientRect();

               const windowLeft = rect.left - containerRect.left;
               const windowRight = rect.right - containerRect.left;
               const windowTop = rect.top - containerRect.top;
               const windowBottom = rect.bottom - containerRect.top;

               if (windowRight > 0 && windowLeft < containerWidth &&
                  windowBottom > 0 && windowTop < containerHeight) {

                  windowRects.push({
                     left: Math.max(0, Math.round(windowLeft)),
                     top: Math.max(0, Math.round(windowTop)),
                     right: Math.min(containerWidth, Math.round(windowRight)),
                     bottom: Math.min(containerHeight, Math.round(windowBottom))
                  });
               }
            }

            availableViewport = this.findLargestAvailableViewport(
               containerWidth,
               containerHeight,
               windowRects,
               defaultPadding
            );
         }

         const topPadding = availableViewport.top;
         const leftPadding = availableViewport.left + 50;
         const rightPadding = containerWidth - availableViewport.right;
         const bottomPadding = containerHeight - availableViewport.bottom;

         if (debug) {
            this.drawDebugOverlays(
               containerRect,
               windowRects,
               availableViewport,
               dataBounds,
               { top: topPadding, bottom: bottomPadding, left: leftPadding, right: rightPadding }
            );
         }

         const [[minLng, minLat], [maxLng, maxLat]] = dataBounds;
         if (minLng >= maxLng || minLat >= maxLat) {
            return;
         }

         this.map.fitBounds(dataBounds, {
            padding: {
               top: topPadding,
               bottom: bottomPadding,
               left: leftPadding,
               right: rightPadding
            },
            maxZoom: 19,
            duration: 1000
         });

      } catch (err: any) {
         const evv = new CustomEvent('mit-error-handler', { detail: err.message || err });
         document.body.dispatchEvent(evv);
      }
   }

   private findLargestAvailableViewport(
      containerWidth: number,
      containerHeight: number,
      windowRects: Array<{ left: number; top: number; right: number; bottom: number }>,
      minPadding: number
   ): { left: number; top: number; right: number; bottom: number } {
      type Rect = { left: number; top: number; right: number; bottom: number };

      const inner: Rect = {
         left: minPadding,
         top: minPadding,
         right: containerWidth - minPadding,
         bottom: containerHeight - minPadding
      };

      const isValidRect = (r: Rect) => r.right > r.left && r.bottom > r.top;
      if (!isValidRect(inner)) {
         return { left: 0, top: 0, right: containerWidth, bottom: containerHeight };
      }
      if (windowRects.length === 0) return inner;

      const intersect = (a: Rect, b: Rect): Rect | null => {
         const r: Rect = {
            left: Math.max(a.left, b.left),
            top: Math.max(a.top, b.top),
            right: Math.min(a.right, b.right),
            bottom: Math.min(a.bottom, b.bottom)
         };
         return isValidRect(r) ? r : null;
      };

      const obstacles: Rect[] = [];
      const bounds: Rect = { left: 0, top: 0, right: containerWidth, bottom: containerHeight };

      for (const w of windowRects) {
         const clamped = intersect(w as Rect, bounds);
         if (!clamped) continue;

         const expanded: Rect = {
            left: clamped.left - minPadding,
            top: clamped.top - minPadding,
            right: clamped.right + minPadding,
            bottom: clamped.bottom + minPadding
         };
         const obstacle = intersect(expanded, inner);
         if (obstacle) obstacles.push(obstacle);
      }

      if (obstacles.length === 0) return inner;

      const xCoords: number[] = [inner.left, inner.right];
      const yCoords: number[] = [inner.top, inner.bottom];
      for (const o of obstacles) {
         xCoords.push(o.left, o.right);
         yCoords.push(o.top, o.bottom);
      }

      xCoords.sort((a, b) => a - b);
      yCoords.sort((a, b) => a - b);

      const uniqSorted = (arr: number[]) => {
         const out: number[] = [];
         for (const v of arr) {
            if (out.length === 0 || out[out.length - 1] !== v) out.push(v);
         }
         return out;
      };

      const xs = uniqSorted(xCoords);
      const ys = uniqSorted(yCoords);

      const cols = xs.length - 1;
      const rows = ys.length - 1;
      if (cols <= 0 || rows <= 0) return inner;

      const xIndex = new Map<number, number>();
      const yIndex = new Map<number, number>();
      xs.forEach((v, i) => xIndex.set(v, i));
      ys.forEach((v, i) => yIndex.set(v, i));

      const stride = cols + 1;
      const diff = new Int32Array((rows + 1) * (cols + 1));

      const addRect = (x0: number, y0: number, x1: number, y1: number) => {
         diff[y0 * stride + x0] += 1;
         diff[y0 * stride + x1] -= 1;
         diff[y1 * stride + x0] -= 1;
         diff[y1 * stride + x1] += 1;
      };

      for (const o of obstacles) {
         const x0 = xIndex.get(o.left);
         const x1 = xIndex.get(o.right);
         const y0 = yIndex.get(o.top);
         const y1 = yIndex.get(o.bottom);
         if (x0 === undefined || x1 === undefined || y0 === undefined || y1 === undefined) continue;
         if (x1 <= x0 || y1 <= y0) continue;
         addRect(x0, y0, x1, y1);
      }

      for (let y = 0; y <= rows; y++) {
         let run = 0;
         const rowOff = y * stride;
         for (let x = 0; x <= cols; x++) {
            run += diff[rowOff + x];
            diff[rowOff + x] = run;
         }
      }
      for (let x = 0; x <= cols; x++) {
         let run = 0;
         for (let y = 0; y <= rows; y++) {
            const idx = y * stride + x;
            run += diff[idx];
            diff[idx] = run;
         }
      }

      const heights = new Int32Array(cols);
      let bestArea = 0;
      let best: Rect = inner;

      const stack: number[] = [];

      for (let y = 0; y < rows; y++) {
         for (let x = 0; x < cols; x++) {
            const covered = diff[y * stride + x] > 0;
            heights[x] = covered ? 0 : (heights[x] + 1);
         }

         stack.length = 0;
         for (let x = 0; x <= cols; x++) {
            const currentH = x === cols ? 0 : heights[x];
            while (stack.length > 0) {
               const topIndex = stack[stack.length - 1];
               const topH = heights[topIndex];
               if (topH <= currentH) break;

               stack.pop();
               if (topH <= 0) continue;

               const right = x - 1;
               const left = stack.length > 0 ? (stack[stack.length - 1] + 1) : 0;

               const x0 = xs[left];
               const x1 = xs[right + 1];
               const y1 = ys[y + 1];
               const y0 = ys[y - topH + 1];

               const area = (x1 - x0) * (y1 - y0);
               if (area > bestArea) {
                  bestArea = area;
                  best = { left: x0, top: y0, right: x1, bottom: y1 };
               }
            }
            stack.push(x);
         }
      }

      return isValidRect(best) ? best : inner;
   }

   public newPoiLayer(): MitLayerHandle {
      return new MitLayerHandleMaplibre();
   }

   public openPopup() {
      // let popup=L.popup({
      //    closeButton:false,
      //    closeOnClick:true,
      //    className:"mit-report-popup"
      // });
      // popup.setContent(val);
      // popup.setLatLng(latlng);
      // popup.openOn(this.map);
   }

   public getPoints(): MitLatLng[] {
      return [];
      // let result:MitLatLng[] = [];
      // this.map.eachLayer(function (layer:L.Layer) {
      //   // Lat, long of current point
      //   if (layer instanceof L.Marker) {
      //     let layer2:L.Marker = layer as L.Marker;
      //     result.push(layer2.getLatLng());
      //   } else {
      //     if (layer instanceof L.CircleMarker) {
      //       let layer3:L.CircleMarker = layer as L.CircleMarker;
      //       result.push(layer3.getLatLng());  
      //     }
      //   }
      // });
      // return result;
   }

   public remove() {
      this.map.remove();
   }

   on(event: string, handler: any): void;
   on(event: string, layerid: string, handler: any)
   on(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.on(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.on(event, layeHandler)
   }

   off(event: string, handler: any): void;
   off(event: string, layerid: string, handler: any)
   off(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.off(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.off(event, layeHandler)
   }

   once(event: string, handler: any): void;
   once(event: string, layerid: string, handler: any)
   once(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.once(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.once(event, layeHandler)
   }

   getBounds() {
      return this.map.getBounds()
   }
   getBearing(): number {
      return this.map && this.map.getBearing() || 0;
   }
   getPitch(): number {
      return this.map && this.map.getPitch() || 0;
   }
   getCanvas() {
      return this.map.getCanvas()
   }
   queryRenderedFeatures(geometryOrOptions?, options?) {
      return this.map.queryRenderedFeatures(geometryOrOptions, options)
   }

   public setLayoutProperty(layer: MitLayerHandleMaplibre, property) {
      layer.getLayerIds().map((layerId) => {
         Object.keys(property).forEach((key) =>
            this.map.setLayoutProperty(layerId, key, property[key])
         )
      })
   }

   public setPaintProperty(layer: MitLayerHandleMaplibre, style) {
      layer.getLayerIds().map((layerId) => {
         Object.keys(style).forEach((key) =>
            this.map.setPaintProperty(layerId, key, style[key])
         )
      })
   }

   public hasImage(id: string) { return this.map.hasImage(id) }
   public loadImage(url: string) { return this.map.loadImage(url) }
   public addImage(id: string, image: HTMLImageElement | ImageBitmap | ImageData | { width: number; height: number; data: Uint8Array | Uint8ClampedArray; } | M.StyleImageInterface) { this.map.addImage(id, image) }

   public renderCatchments() {
      // if (this.catchment) { 
      //    this.catchment.clearLayers();
      // } else {
      //    this.catchment = L.layerGroup(undefined, {}).addTo(this.map);
      // }

      // // let polygons:any[] = polygons.features;
      // let jsonForOneFeature;
      // let largestBounds;
      // let ordered:any[]=[];
      // const opacity = 0.75;
      // polygons && polygons.forEach((itm) => {
      //     let time = itm.properties.time;
      //     let timeindex = travelTimes.findIndex((value)=> { return value === time; });
      //     ordered[timeindex]=itm;
      // });
      // let reversed = ordered.reverse();
      // reversed && reversed.forEach((item, idx) => {
      //     let time = item.properties.time;
      //     let timeindex = travelTimes.findIndex((value)=> { return value === time; });
      //     let color = colorScale[timeindex];
      //     let layer = L.geoJSON(undefined, {
      //         style: (feature:any) => {
      //             return {
      //                 fillColor : color,
      //                 fillOpacity : opacity,
      //                 color : color,
      //                 opacity: opacity,
      //                 stroke : true,
      //                 fill : true,
      //                 weight: 2
      //             };
      //         }
      //       }).addTo(this.catchment);
      //     jsonForOneFeature = {
      //       //   ...result, // this is to include crs and properties from the original data
      //         features: [item]
      //     };
      //     layer.addData(jsonForOneFeature);
      //     // The first layer is the largest
      //     if (!largestBounds) {
      //         largestBounds = layer.getBounds();
      //     }
      // });
      // this.map.fitBounds(largestBounds);
   }

   public hideCatchments() {
      if (this.catchment) {
         this.catchment.clearLayers(this);
      }
   }

   public getZoom() {
      return this.map && this.map.getZoom() || 0;
   }

   public getCenter() {
      return MitLatLng.fromM(this.map.getCenter());
   }

   public panTo(location: MitLatLng) {
      this.map?.panTo(location.getLngLat());
   }

   public setZoom(zoom: number) {
      return this.map && this.map.setZoom(zoom);
   }

   public getLayers(): string[] {
      const layers = this.map.getStyle().layers;
      return layers.map(function (layer) {
         return layer.id;
      });
   }

   public removeLayer(layerHandle: MitLayerHandleMaplibre) {
      // remove layers first, then source
      layerHandle.getLayersConsumingEvents().forEach(layerId => {
         GenerateGeom.removeEventListeners(this.map, layerId);
      });
      layerHandle.getLayers()?.forEach((layerId) => {
         if (this.map?.getLayer(layerId.id)) {
            this.map.removeLayer(layerId.id);
         }
      });
      if (layerHandle.hasSource()) {
         if (this.map.getSource(layerHandle.getSourceId())) {
            this.map.removeSource(layerHandle.getSourceId());
         }
      }
   }

   public addLayer(layerHandle: MitLayerHandleMaplibre)
   public addLayer(layerHandle: MitLayerHandleMaplibre, beforeLayerHandles: MitLayerHandleMaplibre[])
   public addLayer(layerHandle: MitLayerHandleMaplibre, beforeLayerHandles?: MitLayerHandleMaplibre[]) {
      let layerSource = layerHandle.getSource();
      if (!this.map.getSource(layerHandle.getSourceId()) && layerSource) {
         this.map.addSource(layerHandle.getSourceId(), layerSource);
      }
      layerHandle.getLayers()?.forEach((layer) => {
         let dataLayerIds = this.getLayers().filter((a) => a.includes("layer-id"));
         let layerID = layer.id
         let layerPriKey = PriotisedMapLayerTypes.findIndex((a) => layerID.includes(a))
         let beforeTheseTypes = [...PriotisedMapLayerTypes].splice(0, layerPriKey + 1).filter((a) => a !== PriotisedMapLayerTypes[layerPriKey])
         let beforeThis: string | undefined = undefined;

         let layers = beforeLayerHandles?.map((a) => {
            const acceptedLayers = a.getLayerIds().find((a) => {
               const hpriKey = PriotisedMapLayerTypes.findIndex((b) => a.includes(b))
               return layerPriKey == hpriKey
            })
            return acceptedLayers;
         })
         beforeThis = layers && layers.find((a) => a !== undefined);



         beforeThis ??= dataLayerIds.find((a) => beforeTheseTypes.some((before) => a.includes(before)))
         if (this.map.getLayer(layer.id)) {
            this.map.removeLayer(layer.id)
         }
         if (beforeThis) {
            this.map?.addLayer(layer, beforeThis);
            return
         }
         this.map?.addLayer(layer);
      });
      layerHandle.layersWithPieCharts.forEach((sourceId) => {
         if (this.markersBySourceId[sourceId]) {
            Object.keys(this.markersBySourceId[sourceId]).forEach((marker) => {
               // clear before removing refs
               this.markersBySourceId[sourceId][marker].remove()
            })
         }
         this.markersBySourceId[sourceId] = {}
      })
   }

   public updateLayer(layerHandle: MitLayerHandleMaplibre) {
      let layerSource = layerHandle.getSource();
      if (layerHandle.hasSource() && this.map && layerSource) {
         if (this.map.getSource(layerHandle.getSourceId())) {
            if (layerSource?.type == 'geojson') {
               (this.map as any).getSource(layerHandle.getSourceId() as any)!.setData(layerSource.data);
            } else if (layerSource?.type == 'image') {
               (this.map as any).getSource(layerHandle.getSourceId() as any)!.setCoordinates(layerSource.coordinates);
            }
         } else {
            this.map.addSource(layerHandle.getSourceId(), layerSource);
         }
      }
      if (layerHandle.hasLayers() && this.map) {
         layerHandle.getLayers().forEach((layer) => {
            if (this.map.getLayer(layer.id)) {
               this.map.removeLayer(layer.id);
            }
            this.map.addLayer(layer);
         });
      }
   }

   public moveLayer(layerHandle: MitLayerHandleMaplibre) {
      // layerHandle.hasSource() && this.map && this.map.addSource(layerHandle.getSourceId(), layerHandle.getSource());
      layerHandle.getLayers()?.forEach((layer) => {
         let dataLayerIds = this.getLayers().filter((a) => a.includes("layer-id"));
         let layerID = layer.id
         let layerPriKey = PriotisedMapLayerTypes.findIndex((a) => layerID.includes(a))
         let beforeTheseTypes = [...PriotisedMapLayerTypes].splice(0, layerPriKey + 1).filter((a) => a !== PriotisedMapLayerTypes[layerPriKey])
         let beforeThis: string | undefined = undefined;

         beforeThis ??= dataLayerIds.find((a) => beforeTheseTypes.some((before) => a.includes(before)))
         if (beforeThis) {
            this.map?.moveLayer(layerID, beforeThis);
            return
         }
         this.map?.moveLayer(layerID);
      });
   }

   public zoomToPoint(latlng: MitLatLng) {
      // let p:MitLatLngExpression = point;
      // let bounds: MitLatLngBoundsExpression = MitLatLngBounds(p, p);
      // // zoom to the area
      // this.map.fitBounds(bounds);
      this.map?.panTo(latlng.getLngLat());
   }

   public createCircle(): MitLayerHandle {
      // let locationMarkers = new L.FeatureGroup();
      // let circle = L.circle(latlng, radius);
      // circle.addTo(locationMarkers);
      // locationMarkers.addTo(this.map);
      // return locationMarkers;
      return new MitLayerHandleMaplibre();
   }

   public removeLayerByHandle(handle: MitLayerHandle) {
      this.removeLayer(handle);
   }

   public locate() {
      // this.map!.locate({setView: false, watch: true, maxZoom: 16});
   }

   public initializeBackgroundLayers() {
      // todo: implement
   }

   private createWMSSource(fl: FeatureLayer): M.RasterSourceSpecification {
      return {
         'type': 'raster',
         // use the tiles option to specify a WMS tile source URL
         'tiles': [
            fl.getTileURL()
         ],
         'tileSize': 256,
         'maxzoom': fl.layer.maxZoom,
         'minzoom': fl.layer.minZoom,
      }
   }

   private createWMSLayer(id: string, source: string, fl: FeatureLayer) {
      return {
         'id': id,
         'type': 'raster',
         'source': source,
         'paint': fl.layer.paint || {},
         'maxzoom': fl.layer.maxZoom,
         'minzoom': fl.layer.minZoom,
      }
   }

   public addVectorLayer(layer: VectorLayer | GeojsonWFS): MitLayerHandle {
      const sourceSpec = layer.getSource() as M.GeoJSONSourceSpecification;
      const layerSpec = layer.getLayers() as M.LayerSpecification[];
      if (this.isInitialized()) {
         if (!this.map.getSource(layer.getSourceID()))
            this.map.addSource(layer.getSourceID(), sourceSpec as any)
         let beforeThisLayer = this.map.getStyle().layers.map((a) => a.id).find((a) => a.startsWith("layer-id"))
         layerSpec.map((a, idx) => {
            if (!this.map.getLayer(layer.getLayerID(idx)))
               this.map.addLayer(a as any, beforeThisLayer);
         })
      }
      const handle = new MitLayerHandleMaplibre();
      handle.addSource(layer.getSourceID(), sourceSpec)
      if (layer instanceof GeojsonWFS) {
         let bounds = this.map.getBounds();
         let zoom = this.map.getZoom();
         if (layer.layer.minZoom < zoom) {
            this.loadingMoveEnd = true
            fetch(layer.getDataSrc(bounds)).then(async (data) => {
               let json
               if (data.headers.get("Content-Type")?.includes("xml")) {
                  if ("wfs" in layer.layer && typeof layer.layer.wfs == "object" && layer.layer.wfs) {
                     if ("wfs" in layer.layer && layer.layer.wfs["key"] && layer.layer.wfs["type"]) {
                        let typeDesc = await DC.WFS.getFeatureType(layer.layer.wfs["key"] as any, layer.layer.wfs["type"] as any)
                        json = WFStoGeoJson(await data.text(), typeDesc).at(0)?.geoJson || turf.featureCollection([]);
                     }
                  } else {
                     json = WFStoGeoJson(await data.text()).at(0)?.geoJson;
                  }
               } else if (
                  data.headers.get("Content-Type")?.includes("application/json") &&
                  data.headers.get("Content-Type")?.includes("ISO-8859-1")
               ) {
                  let decoder = new TextDecoder("ISO-8859-1")
                  json = await JSON.parse(decoder.decode(await data.arrayBuffer()))
               } else {
                  json = await data.json()
               }
               if (json?.crs?.properties?.name?.includes?.("25832")) {
                  json = GenerateGeomUtils.GeoJsonCastToFeatureCollection(json, "EPSG:25832")
               }
               (this.map.getSource(layer.getSourceID()) as M.GeoJSONSource | undefined)?.setData(
                  await (layer.getDataTransformer())(json, { zoom: zoom, bounds: bounds })
               )
               this.loadingMoveEnd = false
            })
         }
         this.moveendFeatureLayer.push({
            sourceID: layer.getSourceID(),
            minZoom: layer.layer.minZoom,
            dataGetter: async (bounds) => {
               try {
                  let data = (await fetch(layer.getDataSrc(bounds)))
                  if (data.headers.get("Content-Type")?.includes("xml")) {
                     if ("wfs" in layer.layer && typeof layer.layer.wfs == "object" && layer.layer.wfs) {
                        if ("wfs" in layer.layer && layer.layer.wfs["key"] && layer.layer.wfs["type"]) {
                           let typeDesc = await DC.WFS.getFeatureType(layer.layer.wfs["key"] as any, layer.layer.wfs["type"] as any)
                           return WFStoGeoJson(await data.text(), typeDesc).at(0)?.geoJson || turf.featureCollection([]);
                        }
                     }
                     return WFStoGeoJson(await data.text()).at(0)?.geoJson || turf.featureCollection([]);
                  } else if (data.headers.get("Content-Type")?.includes("application/json")) {
                     let json
                     if (data.headers.get("Content-Type")?.includes("ISO-8859-1")) {
                        let decoder = new TextDecoder("ISO-8859-1")
                        json = await JSON.parse(decoder.decode(await data.arrayBuffer()))
                     } else {
                        json = await data.json()
                     }
                     if (json?.crs?.properties?.name?.includes?.("25832")) {
                        json = GenerateGeomUtils.GeoJsonCastToFeatureCollection(json, "EPSG:25832")
                     }
                     return json
                  } else {
                     throw new Error("Unknown content type")
                  }
               } catch (err) {
                  console.error(err)
                  let message = ErrorByTileSource(layer.getDataSrc(bounds))
                  document.getElementById("NonBlockingError")?.dispatchEvent(new CustomEvent("NonBlockingError", { "detail": message }))
               }
               return turf.featureCollection([])
            },
            dataTransformer: layer.getDataTransformer()
         })
      }
      layerSpec.map((_a, idx) => {
         handle.addLayer(layer.getLayerID(idx));
      })
      return handle
   }

   public addFeatureLayer(key: string, fl: FeatureLayer): MitLayerHandle {
      let layerId = this.constructBackgroundLayerId(key);
      let sourceId = this.constructBackgroundLayerSourceId(key);
      let sourceSpec = this.createWMSSource(fl);
      let layerSpec = this.createWMSLayer(layerId, sourceId, fl);

      let before = fl.layer.before
      if (this.isInitialized()) {
         if (!this.map.getSource(sourceId))
            this.map.addSource(sourceId, sourceSpec as any);
         let beforeThisLayer = this.map.getStyle().layers.map((a) => a.id).find((a) => a.startsWith("layer-id") || (before && a == before))
         if (!this.map.getLayer(layerId))
            this.map.addLayer(layerSpec as any, beforeThisLayer);
      }

      let handle = new MitLayerHandleMaplibre();
      handle.addSource(sourceId, sourceSpec);
      handle.addLayer(layerId);

      return handle;
   }


   public searchKey<T extends T[] | string>(key, nestedArray: T): boolean {
      if (Array.isArray(nestedArray)) {
         for (let i = 0; i < nestedArray.length; i++) {
            if (this.searchKey(key, nestedArray[i])) {
               return true;
            }
         }
      } else {
         if (nestedArray === ("@" + key)) {
            return true;
         }
      }
      return false;
   }


   public updateVectorLayer(layer: VectorLayer | GeojsonWFS, value: { [key: string]: any }): MitLayerHandle {
      const keys = Object.keys(value)
      let layerSpec = layer.getLayers()
      //TODO: Find what needs be changed
      for (let i = 0; i < layerSpec.length; i++) {
         let preTrans = layer.layer.layers[i] as any
         let transformed = layerSpec[i] as any
         // filter
         if ("filter" in layerSpec[i]) {
            this.map.setFilter(layer.getLayerID(i), transformed.filter, { validate: true })
         } else {
            this.map.setFilter(layer.getLayerID(i))
         }
         // paint
         let paintKeys = preTrans.paint && Object.keys(preTrans.paint) || []
         for (let j = 0; j < paintKeys.length; j++) {
            if (keys.some((key) => this.searchKey(key, preTrans.paint[paintKeys[j]]))) {
               this.map.setPaintProperty(layer.getLayerID(i), paintKeys[j], transformed.paint[paintKeys[j]])
            }
         }
         // layout
         let layoutKeys = preTrans.layout && Object.keys(preTrans.layout) || []
         for (let j = 0; j < layoutKeys.length; j++) {
            if (keys.some((key) => this.searchKey(key, preTrans.layout[layoutKeys[j]]))) {
               this.map.setLayoutProperty(layer.getLayerID(i), layoutKeys[j], transformed.layout[layoutKeys[j]])
            }
         }
      }

      let handle = new MitLayerHandleMaplibre();
      let sourceSpec = layer.getSource() as M.GeoJSONSourceSpecification;
      handle.addSource(layer.getSourceID(), sourceSpec);
      layerSpec.map((_a, idx) => {
         handle.addLayer(layer.getLayerID(idx));
      })
      return handle;
   }

   public updateFeatureLayer(key: string, fl: FeatureLayer): MitLayerHandle {
      let layerId = this.constructBackgroundLayerId(key);
      let sourceId = this.constructBackgroundLayerSourceId(key);
      let sourceSpec = this.createWMSSource(fl);
      let layerSpec = this.createWMSLayer(layerId, sourceId, fl);
      if (this.isInitialized()) {
         if (this.map.getSource(sourceId)) {
            Object.keys(layerSpec.paint || {}).forEach((a) => {
               this.map.setPaintProperty(layerId, a, layerSpec.paint[a])
            })
         }
      }
      let handle = new MitLayerHandleMaplibre();
      handle.addSource(sourceId, sourceSpec);
      handle.addLayer(layerId);

      return handle;
   }

   constructBackgroundLayerId(key: string) {
      return "backgroundlayer" + key;
   }
   constructBackgroundLayerSourceId(key: string) {
      return "backgroundlayerSource" + key;
   }

   public changeVectorVisibility(layer: VectorLayer, visibility: "none" | "visible") {
      layer.getLayerID().map((a) => {
         if (this.map?.getLayer(a)) {
            this.map.setLayoutProperty(a, 'visibility', visibility)
         }
      })
   }

   public changeDynamicVisibility(layer: DynamicDataDrivenLayer, visibility: "none" | "visible") {
      let layerID = "data-layer-" + layer.layer.label;
      if (this.map?.getLayer(layerID)) {
         this.map.setLayoutProperty(layerID, 'visibility', visibility)
      }
   }

   public updateDynamicLayer(_key, layer: DynamicDataDrivenLayer, value) {
      if (layer instanceof DynamicDataDrivenLayerPropertyInfo) {
         let textSpec = layer.createTextFieldSpec(value);
         let layerID = "data-layer-" + layer.layer.label;
         this.map.setLayoutProperty(layerID, 'text-field', textSpec);
      }
   }

   public addDynamicLayer(layer: DynamicDataDrivenLayer) {
      let layerID = "data-layer-" + layer.layer.label;
      let sourceID = "data-source-" + layer.layer.label;
      if (!this.map.getSource(sourceID)) {
         this.map.addSource(sourceID, layer.createGeoJsonSource() as any);
      }
      if (!this.map.getLayer(layerID)) {
         this.map.addLayer(layer.createLayer(sourceID, layerID) as any);
      }
      this.updateDynamicLayer(layerID, layer, layer.layer.variantValues);
      let bounds = this.map.getBounds();
      let zoom = this.map.getZoom();
      if (layer.layer.minZoom < zoom) {
         this.loadingMoveEnd = true
         layer.getDataForBBOX(bounds.toArray().flat()).then((json) => {
            (this.map.getSource(sourceID) as M.GeoJSONSource | undefined)?.setData(
               json
            )
            this.loadingMoveEnd = false
         })
      }

      this.moveendFeatureLayer.push({
         sourceID: sourceID,
         minZoom: layer.layer.minZoom,
         dataGetter: async (bounds) => {
            return await layer.getDataForBBOX(bounds.toArray().flat())
         },
         dataTransformer: async (a) => { return a }
      })

   }

   public removeDynamicLayer(layer: DynamicDataDrivenLayer) {
      let layerID = "data-layer-" + layer.layer.label;
      let sourceID = "data-source-" + layer.layer.label

      if (this.map?.getLayer(layerID)) {
         this.map.removeLayer(layerID)
      }
      if (this.map?.getSource(sourceID)) {
         this.map.removeSource(sourceID)
      }
      let x = this.moveendFeatureLayer.findIndex((a) => a.sourceID == sourceID)
      if (x !== -1) {
         this.moveendFeatureLayer.splice(x, 1)
      }
   }

   public removeVectorLayer(layer: VectorLayer | GeojsonWFS) {
      layer.getLayerID().map((a) => {
         if (this.map?.getLayer(a)) {
            this.map.removeLayer(a)
         }
      })
      if (this.map?.getSource(layer.getSourceID())) {
         this.map.removeSource(layer.getSourceID())
      }
      if (layer instanceof GeojsonWFS) {
         let x = this.moveendFeatureLayer.findIndex((a) => a.sourceID == layer.getSourceID())
         if (x !== -1) {
            this.moveendFeatureLayer.splice(x, 1)
         }
      }
   }

   public changeFeatureVisibility(key: string, visibility: "none" | "visible") {
      let a = this.constructBackgroundLayerId(key);
      if (this.map?.getLayer(a)) {
         this.map.setLayoutProperty(a, 'visibility', visibility)
      }
   }

   public removeFeatureLayer(key: string) {
      try {
         let layerId = this.constructBackgroundLayerId(key);
         let sourceId = this.constructBackgroundLayerSourceId(key);
         // Todo: Mapbox does not support event listeners on raster layers. Need a general click handler and control it from there.
         // GenerateGeomMapbox.removeEventListenersForFeatureLayer(this.map, layerId);

         if (this.map?.getLayer(layerId)) {
            this.map.removeLayer(layerId);
         }
         if (this.map?.getSource(sourceId)) {
            this.map.removeSource(sourceId);
         }
      } catch (error) {
         throw new Error(`Could not remove feature layer ${key}: ${error}`);
      }
   }

   public getLatLngFromMouseEvent(clickEvent: any): MitLatLng {
      let e = clickEvent as M.MapMouseEvent;
      return new MitLatLng(e.lngLat.lat, e.lngLat.lng);
   }

   public showUserLocation(show: boolean): void {

      function createPointSource(lat: number, lng: number) {
         return {
            type: "geojson",
            data: {
               "type": "FeatureCollection",
               "features": [circle([lng, lat], 25, { units: "meters" })]
            }
         };
      }

      function createPointLayer(id: string, source: string) {

         return {
            id: id,
            type: 'fill',
            source: source,
            'layout': {
            },
            paint: {
               'fill-color': '#BD9060',
               'fill-opacity': 0.4,
               'fill-outline-color': '#FFFFFF'
            },
         };
      }

      const layer = "userLocationLayer";
      const source = "userLocationSource";
      let errorMsg;
      function getLocation(_map: M.Map, callback: (position) => void) {
         if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(callback, showError);
         } else {
            errorMsg = "Geolocation is not supported by this browser.";
            alert(errorMsg);
         }
      }

      function showPosition(map: M.Map, position: any, obj: MapFacadeMaplibre) {
         errorMsg = "Latitude: " + position.coords.latitude +
            "<br>Longitude: " + position.coords.longitude;
         // alert(errorMsg);
         let pointData = createPointSource(position.coords.latitude, position.coords.longitude);
         if (map.getSource(source)) {
            (map as any).getSource(source).setData(pointData.data);
         } else {
            map.addSource(source, pointData as any);
         }
         if (!map.getLayer(layer)) {
            map.addLayer(createPointLayer(layer, source) as any);
         }
         obj.zoomToData(pointData.data);
      }

      function showError(error) {
         switch (error.code) {
            case error.PERMISSION_DENIED:
               errorMsg = "User denied the request for Geolocation."
               alert(errorMsg);
               break;
            case error.POSITION_UNAVAILABLE:
               errorMsg = "Location information is unavailable."
               alert(errorMsg);
               break;
            case error.TIMEOUT:
               errorMsg = "The request to get user location timed out."
               alert(errorMsg);
               break;
            case error.UNKNOWN_ERROR:
               errorMsg = "An unknown error occurred."
               alert(errorMsg);
               break;
         }
      }
      if (show) {
         if (this.map) {
            getLocation(this.map, (position) => { showPosition(this.map, position, this) });
         }
      } else {
         // alert("switch off");
         if (this.map && this.map.getLayer && this.map.getLayer(layer)) {
            this.map.removeLayer(layer);
         }
      }
   }

   public replacePaint(a, b) {
      let props = new Set([...Object.keys(a.paint || {}), ...Object.keys(b || {})]);
      props.forEach((prop) => {
         if (b?.[prop] !== undefined) {
            this.map.setPaintProperty(a.id, prop, b[prop]);
         } else {
            this.map.setPaintProperty(a.id, prop, undefined);
         }
      })
   }

   public replaceLayout(a, b) {
      let props = new Set([...Object.keys(a.layout || {}), ...Object.keys(b || {})]);
      props.forEach((prop) => {
         if (b?.[prop] !== undefined) {
            this.map.setLayoutProperty(a.id, prop, b[prop]);
         } else {
            this.map.setLayoutProperty(a.id, prop, undefined);
         }
      })
   }

   public replaceFilter(a, b?) {
      if (b !== undefined) {
         this.map.setFilter(a.id, b);
      } else {
         this.map.setFilter(a.id, undefined);
      }
   }

   public replaceMinMax(a, b?, c?) {
      this.map.setLayerZoomRange(a.id, b, c);
   }

   /**
    * Clears all debug overlay elements
    */
   private clearDebugOverlays() {
      const existingOverlays = document.querySelectorAll('.zoom-debug-overlay');
      existingOverlays.forEach(el => el.remove());
   }

   private drawDebugOverlays(
      containerRect: DOMRect,
      windowRects: Array<{ left: number; top: number; right: number; bottom: number }>,
      availableViewport: { left: number; top: number; right: number; bottom: number },
      dataBounds: [[number, number], [number, number]],
      padding: { top: number; bottom: number; left: number; right: number }
   ) {
      if (!this.map) return;

      const mapContainer = this.map.getContainer();

      let overlayContainer = document.getElementById('zoom-debug-overlay-container');
      if (!overlayContainer) {
         overlayContainer = document.createElement('div');
         overlayContainer.id = 'zoom-debug-overlay-container';
         overlayContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 10000;
         `;
         mapContainer.style.position = 'relative';
         mapContainer.appendChild(overlayContainer);
      }

      const containerOutline = document.createElement('div');
      containerOutline.className = 'zoom-debug-overlay';
      containerOutline.style.cssText = `
         position: absolute;
         left: 0;
         top: 0;
         width: ${containerRect.width}px;
         height: ${containerRect.height}px;
         border: 2px solid #0000ff;
         box-sizing: border-box;
         pointer-events: none;
      `;
      overlayContainer.appendChild(containerOutline);

      windowRects.forEach((windowRect, index) => {
         const windowOverlay = document.createElement('div');
         windowOverlay.className = 'zoom-debug-overlay';
         windowOverlay.style.cssText = `
            position: absolute;
            left: ${windowRect.left}px;
            top: ${windowRect.top}px;
            width: ${windowRect.right - windowRect.left}px;
            height: ${windowRect.bottom - windowRect.top}px;
            border: 2px solid #ff0000;
            background-color: rgba(255, 0, 0, 0.2);
            box-sizing: border-box;
            pointer-events: none;
         `;
         overlayContainer.appendChild(windowOverlay);

         const windowLabel = document.createElement('div');
         windowLabel.className = 'zoom-debug-overlay';
         windowLabel.textContent = `Window ${index + 1}`;
         windowLabel.style.cssText = `
            position: absolute;
            left: ${windowRect.left + 5}px;
            top: ${windowRect.top + 5}px;
            background-color: rgba(255, 0, 0, 0.8);
            color: white;
            padding: 2px 5px;
            font-size: 11px;
            font-family: monospace;
            pointer-events: none;
            z-index: 10001;
         `;
         overlayContainer.appendChild(windowLabel);
      });

      const viewportOverlay = document.createElement('div');
      viewportOverlay.className = 'zoom-debug-overlay';
      viewportOverlay.style.cssText = `
         position: absolute;
         left: ${availableViewport.left}px;
         top: ${availableViewport.top}px;
         width: ${availableViewport.right - availableViewport.left}px;
         height: ${availableViewport.bottom - availableViewport.top}px;
         border: 3px solid #00ff00;
         background-color: rgba(0, 255, 0, 0.15);
         box-sizing: border-box;
         pointer-events: none;
      `;
      overlayContainer.appendChild(viewportOverlay);

      const viewportLabel = document.createElement('div');
      viewportLabel.className = 'zoom-debug-overlay';
      viewportLabel.textContent = `Available Viewport (${Math.round(availableViewport.right - availableViewport.left)}×${Math.round(availableViewport.bottom - availableViewport.top)})`;
      viewportLabel.style.cssText = `
         position: absolute;
         left: ${availableViewport.left + 5}px;
         top: ${availableViewport.top + 5}px;
         background-color: rgba(0, 255, 0, 0.9);
         color: black;
         padding: 3px 6px;
         font-size: 12px;
         font-weight: bold;
         font-family: monospace;
         pointer-events: none;
         z-index: 10002;
      `;
      overlayContainer.appendChild(viewportLabel);

      const paddingLabels = [
         { text: `Top: ${Math.round(padding.top)}px`, x: containerRect.width / 2, y: padding.top / 2, align: 'center' },
         { text: `Bottom: ${Math.round(padding.bottom)}px`, x: containerRect.width / 2, y: containerRect.height - padding.bottom / 2, align: 'center' },
         { text: `Left: ${Math.round(padding.left)}px`, x: padding.left / 2, y: containerRect.height / 2, align: 'center', rotate: true },
         { text: `Right: ${Math.round(padding.right)}px`, x: containerRect.width - padding.right / 2, y: containerRect.height / 2, align: 'center', rotate: true }
      ];

      paddingLabels.forEach(label => {
         const paddingLabel = document.createElement('div');
         paddingLabel.className = 'zoom-debug-overlay';
         paddingLabel.textContent = label.text;
         paddingLabel.style.cssText = `
            position: absolute;
            left: ${label.x}px;
            top: ${label.y}px;
            transform: ${label.rotate ? 'translate(-50%, -50%) rotate(-90deg)' : 'translate(-50%, -50%)'};
            background-color: rgba(255, 255, 0, 0.9);
            color: black;
            padding: 2px 5px;
            font-size: 11px;
            font-family: monospace;
            pointer-events: none;
            z-index: 10003;
            white-space: nowrap;
         `;
         overlayContainer.appendChild(paddingLabel);
      });

      setTimeout(() => {
         try {
            const [[minLng, minLat], [maxLng, maxLat]] = dataBounds;
            const topLeft = this.map!.project([minLng, maxLat]);
            const bottomRight = this.map!.project([maxLng, minLat]);

            const dataBoxLeft = Math.min(topLeft.x, bottomRight.x);
            const dataBoxTop = Math.min(topLeft.y, bottomRight.y);
            const dataBoxWidth = Math.abs(bottomRight.x - topLeft.x);
            const dataBoxHeight = Math.abs(bottomRight.y - topLeft.y);

            if (dataBoxLeft < containerRect.width && dataBoxLeft + dataBoxWidth > 0 &&
               dataBoxTop < containerRect.height && dataBoxTop + dataBoxHeight > 0) {

               const dataBoxOverlay = document.createElement('div');
               dataBoxOverlay.className = 'zoom-debug-overlay';
               dataBoxOverlay.style.cssText = `
                  position: absolute;
                  left: ${dataBoxLeft}px;
                  top: ${dataBoxTop}px;
                  width: ${dataBoxWidth}px;
                  height: ${dataBoxHeight}px;
                  border: 2px dashed #ff00ff;
                  background-color: rgba(255, 0, 255, 0.1);
                  box-sizing: border-box;
                  pointer-events: none;
               `;
               overlayContainer.appendChild(dataBoxOverlay);

               const dataBoxLabel = document.createElement('div');
               dataBoxLabel.className = 'zoom-debug-overlay';
               dataBoxLabel.textContent = `Data BBox`;
               dataBoxLabel.style.cssText = `
                  position: absolute;
                  left: ${Math.max(5, Math.min(dataBoxLeft + 5, containerRect.width - 80))}px;
                  top: ${Math.max(5, Math.min(dataBoxTop + 5, containerRect.height - 20))}px;
                  background-color: rgba(255, 0, 255, 0.9);
                  color: white;
                  padding: 2px 5px;
                  font-size: 11px;
                  font-family: monospace;
                  pointer-events: none;
                  z-index: 10004;
               `;
               overlayContainer.appendChild(dataBoxLabel);
            }
         } catch (err) {
            console.warn('Could not draw data bounding box overlay:', err);
         }
      }, 1100);

      const infoPanel = document.createElement('div');
      infoPanel.className = 'zoom-debug-overlay';
      infoPanel.innerHTML = `
         <div style="background: rgba(0, 0, 0, 0.8); color: white; padding: 10px; font-family: monospace; font-size: 11px; line-height: 1.4;">
            <div><strong>Debug Info:</strong></div>
            <div>Container: ${Math.round(containerRect.width)}×${Math.round(containerRect.height)}</div>
            <div>Windows: ${windowRects.length}</div>
            <div>Available Viewport: ${Math.round(availableViewport.right - availableViewport.left)}×${Math.round(availableViewport.bottom - availableViewport.top)}</div>
            <div>Padding: T:${Math.round(padding.top)} B:${Math.round(padding.bottom)} L:${Math.round(padding.left)} R:${Math.round(padding.right)}</div>
            <div style="margin-top: 5px; font-size: 10px; opacity: 0.8;">
               <div style="display: inline-block; width: 12px; height: 12px; background: #0000ff; border: 1px solid white; margin-right: 5px;"></div>Container<br/>
               <div style="display: inline-block; width: 12px; height: 12px; background: #ff0000; border: 1px solid white; margin-right: 5px;"></div>Windows<br/>
               <div style="display: inline-block; width: 12px; height: 12px; background: #00ff00; border: 1px solid white; margin-right: 5px;"></div>Available Viewport<br/>
               <div style="display: inline-block; width: 12px; height: 12px; background: #ff00ff; border: 1px solid white; margin-right: 5px;"></div>Data BBox
            </div>
         </div>
      `;
      infoPanel.style.cssText = `
         position: absolute;
         top: 10px;
         right: 10px;
         pointer-events: none;
         z-index: 10005;
      `;
      overlayContainer.appendChild(infoPanel);
   }
}

export class CircleSelector implements CircleSelectorAbstract {
   map: M.Map;
   callbackOnComplete: (latlng: MitLatLng, distance: number) => void;
   callbackOnMove: (latlng: MitLatLng, distance: number) => void;
   callbackOnCancel: () => void;
   DefaultDistance: number;
   MinimumDistance: number;
   MaximumDistance: number;
   // State:{
   selectionState: SelectionState = SelectionState.Disabled;
   marker: M.Marker | undefined;
   coords: M.LngLat | undefined;
   distance: number | undefined;
   // }
   layer = "circleselector";


   constructor(
      map: M.Map,
      callbackOnComplete: (latlng: MitLatLng, distance: number) => void,
      callbackOnMove: (latlng: MitLatLng, distance: number) => void,
      callbackOnCancel: () => void,
      DefaultDistance: number,
      MinimumDistance: number,
      MaximumDistance: number) {

      this.map = map;
      this.callbackOnComplete = callbackOnComplete;
      this.callbackOnMove = callbackOnMove;
      this.callbackOnCancel = callbackOnCancel;
      this.DefaultDistance = DefaultDistance;
      this.MinimumDistance = MinimumDistance;
      this.MaximumDistance = MaximumDistance;

   }

   public enable() {
      this.selectionState = SelectionState.ClickToSelectCenter;
      this.map.on("click", this.layer, this.mapClickHandler);
   }

   public disable() {
      this.cleanup();
   }

   public isDisabled() {
      return this.selectionState === SelectionState.Disabled;
   }

   public cleanup() {
      if (this.map) {
         this.marker?.remove();
         this.map.off("click", this.layer, this.mapClickHandler);
         this.map.off("mousemove", this.layer, this.mapMoveHandler);
      }



      // this.map && this.map.closePopup();
      this.selectionState = SelectionState.Disabled;
   }

   public remove() {
      if (this.map) {
         this.marker?.remove();
      }
   }

   public show(_latlng?: MitLatLng, _radius?: number) {
      // this.setOrUpdateMarker(latlng, radius);
   }

   public hide() {
      this.remove();
   }

   public getLatLng(): MitLatLng | undefined {
      return this.coords && MitLatLng.fromM(this.coords);
   }



   mapMoveHandler(e2: M.MapMouseEvent) {
      // find the distance between the center and mouse position.
      let f1 = this.coordToTurfFeature(e2.lngLat);
      let f2 = this.coordToTurfFeature(this.marker!.getLngLat());
      let dist = turf.distance(f1, f2, { units: 'kilometers' });
      dist = dist / 1000; // to meters.
      dist = 10 * (Math.floor(dist / 10)); // round to whole 10 meters
      if (!dist) { dist = 1; } // minimum distance of 1 m (otherwise we get error on missing param from servrer)
      if (dist < this.MinimumDistance) { dist = this.MinimumDistance; } // minimum distance of 50 m (otherwise we get error on missing param from servrer)
      if (dist > this.MaximumDistance) { dist = this.MaximumDistance; } // maximum distance of 10000 m (otherwise we get error on missing param from servrer)
      this.setOrUpdateMarker(undefined, dist);
      // ToDo: migrate to Maplibre popup.
      // var popup=L.popup({
      //     closeButton:false,
      //     closeOnClick:true,
      //     className:"mit-report-popup"
      //   });
      // popup.setContent(""+dist.toFixed(0)+"m");
      // popup.setLatLng(e2.latlng);
      // popup.openOn(this.map!);
      this.distance = dist;
      // todo: implement
      // this.callbackOnMove(this.marker!.getLngLat(), dist);
   }

   setOrUpdateMarker(_latlng?: MitLatLng, _radius?: number) {
      // ToDo: migrate to Maplibre.
      // if(this.marker) {
      //     latlng && this.marker.setLatLng(latlng);
      //     radius && this.marker.setRadius(radius);
      // } else {
      //     console.assert(latlng, "Event object expected");
      //     console.assert(radius, "Distance expected");
      //     var marker = L.circle(
      //         latlng!, 
      //         {
      //         radius: radius || 0,
      //         color: 'red'
      //         });
      //     marker.addTo(this.map);
      //     this.marker=marker;
      //     this.coords=latlng!;    
      // }
   }

   mapClickHandler(_e2: any) {
      // ToDo: migrate to Maplibre popup.
      //    switch(this.selectionState) {
      //        case SelectionState.ClickToSelectCenter:
      //            this.setOrUpdateMarker(e2.latlng, this.distance);
      //            this.map.on("mousemove", this.mapMoveHandler, this);
      //            this.selectionState = SelectionState.CenterSelected;
      //            this.callbackOnMove(this.coords, this.distance);
      //            break;
      //        case SelectionState.CenterSelected:
      //            this.map.off("mousemove", this.mapMoveHandler, this);
      //            this.selectionState = SelectionState.RimSelected;
      //            this.callbackOnComplete(this.coords, this.distance);
      //            break;
      //        case SelectionState.RimSelected:
      //            this.remove();
      //            this.selectionState = SelectionState.ClickToSelectCenter;
      //            this.marker = undefined;
      //            this.distance = this.DefaultDistance;
      //            break;
      //        default:
      //            throw Utils.createErrorEventObject("Unknown or unexpected state:"+this.selectionState);
      //    }
   }

   private coordToTurfFeature(coords: M.LngLat) {
      // var geometry:any = {
      //    "type": "Point",
      //    "coordinates": [coords.lng, coords.lat]
      //  };
      //  var feature = turf.feature(geometry);
      var feature = turf.point([coords.lng, coords.lat]);
      return feature;
   }
}

function ErrorMessageMapfacade(source: M.Source) {
   if ("tiles" in source && Array.isArray(source.tiles)) {
      return ErrorByTileSource(source.tiles[0])
   }
   return "Unknown error"
}

function ErrorByTileSource(a: string) {
   const errors = {
      "wms.cowi.com": "Cowi",
      "services.hxgncontent.com": "Hexagon",
      "lb.viamap.net": "Viamap-Geoserver",
      "api.dataforsyningen.dk": "Dataforsyningen",
      "services.datafordeler.dk": "Datafordeler",
      "wfs.datafordeler.dk": "Datafordeler",
      "geofa.geodanmark.dk": "Geodanmark",
      "gisp.bpst.dk": "Social- og Boligstyrelsen",
      "geoserver.plandata.dk": "Plandata",
      "arealeditering-dist-geo.miljoeportal.dk": "Miljøportal",
      "geocloud.vd.dk": "Vejdirektoratet",
      "www.kulturarv.dk": "Kulturarv",
      "wfs2-miljoegis.mim.dk": "Miljøgis",
      "data.geus.dk": "Geus",
      "vmgeoserver.vd.dk": "Vejdirektoratet",
      "b0501-prod-arldgeo-app.azurewebsites.net": "Areal Geo",
      "geodata.fvm.dk": "Landbrugs- og Fiskeristyrelsen",
      "gis.nst.dk": "Naturstyrelsen",
      "miljoegis.mim.dk": "Miljøgis",
      "miljoegis3.mim.dk": "Miljøgis",
      "arld-extgeo.miljoeportal.dk": "Miljøportal",
      "naturarealdatageo.miljoeportal.dk": "Miljøportal"
   }
   let errSource = Object.keys(errors).find((b) => a.includes(b))
   if (errSource) {
      return errors[errSource]
   }
   return "Unknown error"
}

var btdata: Uint8Array;
// oxlint-disable-next-line no-unused-vars
function blankTileData() {
   if (!btdata) {
      btdata = Uint8Array.from(atob("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"), (e) => e.charCodeAt(0));
   }
   return btdata
}

function isWmsImageResponse(response: Response) {
   const contentType = response.headers.get("Content-Type") ?? "";
   if (contentType.includes("image")) {
      return true;
   }
   if (
      contentType.includes("text")
      || contentType.includes("html")
      || contentType.includes("json")
      || contentType.includes("xml")
   ) {
      return false;
   }
   return true;
}

function urlIdentFromUrl(url: string | undefined) {
   if (!url) {
      return ""
   }
   let urlObj = new URL(url);
   return urlObj.hostname + "|" + urlObj.searchParams.get("LAYERS")
}

async function generateTileDataWithText(error?: string, x = 0.5, y = 0.5) {
   let canvas = document.createElement('canvas');
   canvas.width = 256;
   canvas.height = 256;
   let ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
   ctx.fillStyle = 'red';
   ctx.font = '12px Arial';

   let measure = ctx.measureText(error || "Error:");

   ctx.fillText(error || "Error:", (256 - measure.width) * x, (256 - measure.actualBoundingBoxDescent) * y);
   return createImageBitmap(ctx.getImageData(0, 0, 256, 256));
}

function partLayer(id: string) {
   return {
      "id": id,
      "type": "background",
      "layout": { "visibility": "none" }
   }
}